// The processing engine: reading log bytes, parsing, aggregating and persisting.
// It runs inside the analyzer worker, and inside the page itself when the
// browser refuses to start a worker, so the orchestration lives here exactly
// once.
//
// One analyzer instance per file is kept in memory (LRU bounded) so appends only
// cost the new bytes. Snapshots are written to IndexedDB so the same is true
// after a page reload.

import { createAnalyzer, restoreAnalyzer, STATE_VERSION } from './analyzer'
import { buildFileReport, buildSegmentReport } from './reportBuilder'
import {
    readLineChunks,
    computeHeadSignature,
    computeTailSignature,
    verifyResume,
    DEFAULT_CHUNK_SIZE,
} from './fileReader'
import {
    getFileRecord,
    putFileRecord,
    getState,
    putState,
    deleteState,
    putReports,
    trimStates,
    trimReportCache,
    reportCacheStats,
    deleteReports,
    listReportOwners,
    listReports,
    getReport,
    DEFAULT_REPORT_BUDGET_BYTES,
} from './db'

const MAX_LIVE_ANALYZERS = 2
const PROGRESS_INTERVAL_MS = 220
// A resume snapshot of a very long raid log runs into tens of megabytes, and a
// live log grows every few seconds. Writing it on every append would cost far
// more than it saves, so it is written at most this often; a snapshot that lags
// behind the record is still usable, because the reader simply continues from
// the offset the snapshot carries.
const STATE_PERSIST_INTERVAL_MS = 45000

export function createEngine(post) {
    const live = new Map()
    const cancelled = new Set()
    const statePersistedAt = new Map()
    let queue = Promise.resolve()
    let reportBudgetBytes = DEFAULT_REPORT_BUDGET_BYTES

    function trimLiveAnalyzers(keepFileId) {
        if (live.size <= MAX_LIVE_ANALYZERS) return
        const entries = [...live.entries()]
            .filter(([fileId]) => fileId !== keepFileId)
            .sort((left, right) => left[1].touchedAt - right[1].touchedAt)

        while (live.size > MAX_LIVE_ANALYZERS && entries.length) {
            const [fileId] = entries.shift()
            live.delete(fileId)
        }
    }

    // Strips the parts of a segment report that only the detail views need, so the
    // file summary stays small enough to post and store on every tick.
    function summarizeSegment(report) {
        return {
            key: report.key,
            type: report.type,
            name: report.name,
            meta: report.meta,
            startTs: report.startTs,
            endTs: report.endTs,
            durationMs: report.durationMs,
            activeMs: report.activeMs,
            fightMs: report.fightMs,
            basisMs: report.basisMs,
            basis: report.basis,
            eventCount: report.eventCount,
            enemyDeaths: report.enemyDeaths,
            totals: report.totals,
            deathCount: report.deaths.length,
            playerCount: report.players.length,
            roster: report.roster,
        }
    }

    function buildSummary(analyzer, fileMeta) {
        const full = buildFileReport(analyzer)
        const segments = full.segments.map(summarizeSegment)

        return {
            ...fileMeta,
            fileName: full.fileName,
            logVersion: full.logVersion,
            buildVersion: full.buildVersion,
            advancedLogging: full.advancedLogging,
            dateOrder: full.dateOrder,
            firstTs: full.firstTs,
            lastTs: full.lastTs,
            durationMs: full.durationMs,
            lineCount: full.lineCount,
            eventCount: full.eventCount,
            skippedLines: full.skippedLines,
            parseErrors: full.parseErrors,
            zone: full.zone,
            primaryZone: full.primaryZone,
            zones: full.zones,
            deathCount: full.deathCount,
            enemyDeaths: full.enemyDeaths,
            unknownEvents: full.unknownEvents,
            logOwner: full.logOwner,
            segments,
            challengeRuns: full.challengeRuns,
            encounters: full.encounters,
            reports: full.segments,
        }
    }

    async function persist(fileId, analyzer, record, summary, options = {}) {
        const reports = summary.reports
        const storedSummary = { ...summary }
        delete storedSummary.reports

        await putFileRecord({ ...record, summary: storedSummary })
        await putReports(fileId, reports)

        // The file just analyzed is never the one evicted, so a long session on
        // one log can never throw away the report it is showing.
        const trimmed = await trimReportCache(reportBudgetBytes, [fileId])
        if (trimmed.evicted.length) {
            post({ type: 'cacheEvicted', fileIds: trimmed.evicted, bytes: trimmed.bytes })
        }

        const lastWrite = statePersistedAt.get(fileId) || 0
        const dueForState = options.withState === true
            || Date.now() - lastWrite >= STATE_PERSIST_INTERVAL_MS

        if (options.withState !== false && dueForState) {
            try {
                await putState(fileId, analyzer.serialize())
                await trimStates([fileId])
                statePersistedAt.set(fileId, Date.now())
            } catch (error) {
                // A resume snapshot is an optimization: when it does not fit, the
                // file is simply re-analyzed from scratch next time it grows.
                await deleteState(fileId)
                statePersistedAt.delete(fileId)
                post({ type: 'warning', fileId, message: 'Resume state could not be stored; this file will be re-analyzed if it grows.' })
            }
        }

        return storedSummary
    }

    async function loadAnalyzer(fileId, fileName, record, mode) {
        if (mode === 'reset') {
            live.delete(fileId)
            await deleteState(fileId)
            return { analyzer: createAnalyzer({ fileName }), fromOffset: 0, reset: true }
        }

        // The bytes up to `record.offset` have been verified as unchanged, so any
        // analyzer that stopped at or before that point can continue from its own
        // offset; the stretch in between is simply read again into that analyzer.
        const existing = live.get(fileId)
        if (existing && existing.analyzer.state.offset <= record.offset) {
            existing.touchedAt = Date.now()
            return { analyzer: existing.analyzer, fromOffset: existing.analyzer.state.offset, reset: false }
        }

        if (record.offset > 0) {
            const snapshot = await getState(fileId)
            if (snapshot && snapshot.version === STATE_VERSION && snapshot.state.offset <= record.offset) {
                const restored = restoreAnalyzer(snapshot)
                if (restored) {
                    return { analyzer: restored, fromOffset: restored.state.offset, reset: false }
                }
            }
            // No usable snapshot: the only correct option is a clean re-analysis.
            return { analyzer: createAnalyzer({ fileName }), fromOffset: 0, reset: true }
        }

        return { analyzer: createAnalyzer({ fileName }), fromOffset: 0, reset: true }
    }

    async function processFile(message) {
        const { requestId, fileId, fileName, directoryId, file, force } = message
        cancelled.delete(fileId)

        let record = (await getFileRecord(fileId)) || {
            id: fileId,
            directoryId,
            name: fileName,
            size: 0,
            lastModified: 0,
            offset: 0,
            headSignature: '',
            tailSignature: '',
            status: 'new',
            summary: null,
            analyzedAt: 0,
            error: '',
        }

        record = {
            ...record,
            directoryId: directoryId || record.directoryId,
            name: fileName,
            status: 'processing',
            error: '',
        }
        await putFileRecord(record)
        post({ type: 'status', requestId, fileId, status: 'processing', offset: record.offset, size: file.size })

        let mode = 'append'
        if (force) {
            mode = 'reset'
        } else if (record.offset > 0) {
            if (file.size < record.offset) {
                mode = 'reset'
            } else {
                const verified = await verifyResume(record, file)
                mode = verified.mode
            }
        } else {
            mode = 'reset'
        }

        const { analyzer, fromOffset, reset } = await loadAnalyzer(fileId, fileName, record, mode)
        if (reset) {
            record.offset = 0
            record.headSignature = await computeHeadSignature(file)
            post({ type: 'status', requestId, fileId, status: 'processing', offset: 0, size: file.size, reset: true })
        }

        const startedAt = Date.now()
        let lastProgress = 0

        const finalOffset = await readLineChunks(file, {
            fromOffset: reset ? 0 : fromOffset,
            chunkSize: DEFAULT_CHUNK_SIZE,
            shouldAbort: () => cancelled.has(fileId),
            onText: (text) => {
                analyzer.processLines(text)
            },
            onProgress: (offset, size) => {
                analyzer.state.offset = offset
                const now = Date.now()
                if (now - lastProgress >= PROGRESS_INTERVAL_MS) {
                    lastProgress = now
                    post({
                        type: 'progress',
                        requestId,
                        fileId,
                        offset,
                        size,
                        events: analyzer.state.eventCount,
                        elapsedMs: now - startedAt,
                    })
                }
            },
        })

        const aborted = cancelled.has(fileId)
        analyzer.state.offset = finalOffset
        analyzer.state.size = file.size
        analyzer.state.lastModified = file.lastModified

        live.set(fileId, { analyzer, touchedAt: Date.now() })
        trimLiveAnalyzers(fileId)

        record = {
            ...record,
            size: file.size,
            lastModified: file.lastModified,
            offset: finalOffset,
            tailSignature: await computeTailSignature(file, finalOffset),
            status: aborted ? 'updated' : 'ready',
            analyzedAt: Date.now(),
        }

        const summary = buildSummary(analyzer, {
            id: fileId,
            directoryId: record.directoryId,
            name: fileName,
            size: file.size,
            lastModified: file.lastModified,
            offset: finalOffset,
            status: record.status,
            analyzedAt: record.analyzedAt,
            processingMs: Date.now() - startedAt,
        })

        const storedSummary = await persist(fileId, analyzer, record, summary)

        post({
            type: 'done',
            requestId,
            fileId,
            aborted,
            summary: storedSummary,
            elapsedMs: Date.now() - startedAt,
        })
    }

    async function sendSegment(message) {
        const { requestId, fileId, segmentKey } = message
        const entry = live.get(fileId)

        if (entry) {
            entry.touchedAt = Date.now()
            const analyzer = entry.analyzer
            const segment = segmentKey === 'log'
                ? analyzer.logSegment
                : [...analyzer.finishedSegments, ...analyzer.openSegments].find((item) => item.key === segmentKey)

            if (segment) {
                const report = buildSegmentReport(segment, analyzer.registry, { deaths: analyzer.state.deaths })
                if (segmentKey === 'log') report.name = 'Whole log'
                post({ type: 'segmentReport', requestId, fileId, segmentKey, report })
                return
            }
        }

        const stored = await getReport(fileId, segmentKey)
        if (stored) {
            post({ type: 'segmentReport', requestId, fileId, segmentKey, report: stored })
            return
        }

        // Nothing live and nothing cached: the analyzer's own snapshot still
        // holds every segment, so a log that was analyzed before is rebuilt
        // from it instead of being read again. This is what keeps a reopened
        // log from asking to be analyzed a second time.
        const snapshot = await getState(fileId)
        const restored = snapshot && snapshot.version === STATE_VERSION ? restoreAnalyzer(snapshot) : null
        if (restored) {
            live.set(fileId, { analyzer: restored, touchedAt: Date.now() })
            const segment = segmentKey === 'log'
                ? restored.logSegment
                : [...restored.finishedSegments, ...restored.openSegments].find((item) => item.key === segmentKey)

            if (segment) {
                const report = buildSegmentReport(segment, restored.registry, { deaths: restored.state.deaths })
                if (segmentKey === 'log') report.name = 'Whole log'
                post({ type: 'segmentReport', requestId, fileId, segmentKey, report })
                return
            }
        }

        post({ type: 'segmentReport', requestId, fileId, segmentKey, report: null })
    }

    async function sendStoredSummary(message) {
        const { requestId, fileId } = message
        const record = await getFileRecord(fileId)
        post({
            type: 'storedSummary',
            requestId,
            fileId,
            summary: record ? record.summary : null,
            record: record || null,
        })
    }

    async function sendStoredReports(message) {
        const { requestId, fileId } = message
        const reports = await listReports(fileId)
        post({ type: 'storedReports', requestId, fileId, reports })
    }

    async function sendCacheStats(message) {
        const stats = await reportCacheStats()
        post({
            type: 'cacheStats',
            requestId: message.requestId,
            stats: { ...stats, budgetBytes: reportBudgetBytes },
        })
    }

    async function clearReportCache(message) {
        const { requestId, keepFileId } = message
        const before = await reportCacheStats()
        const records = await listReportOwners()
        await Promise.all(records
            .filter((fileId) => fileId !== keepFileId)
            .map((fileId) => deleteReports(fileId)))
        const after = await reportCacheStats()
        post({
            type: 'cacheCleared',
            requestId,
            freedBytes: Math.max(0, before.bytes - after.bytes),
            stats: { ...after, budgetBytes: reportBudgetBytes },
        })
    }

    function enqueue(task) {
        queue = queue.then(task).catch((error) => {
            post({ type: 'error', message: error && error.message ? error.message : String(error) })
        })
        return queue
    }

    return function handle(message) {
        if (!message || !message.type) return

        switch (message.type) {
            case 'process':
                enqueue(() => processFile(message).catch(async (error) => {
                    const record = await getFileRecord(message.fileId)
                    if (record) {
                        await putFileRecord({
                            ...record,
                            status: 'error',
                            error: error && error.message ? error.message : String(error),
                        })
                    }
                    post({
                        type: 'failed',
                        requestId: message.requestId,
                        fileId: message.fileId,
                        message: error && error.message ? error.message : String(error),
                    })
                }))
                break

            case 'segment':
                enqueue(() => sendSegment(message))
                break

            case 'storedSummary':
                enqueue(() => sendStoredSummary(message))
                break

            case 'storedReports':
                enqueue(() => sendStoredReports(message))
                break

            case 'configure':
                if (Number(message.reportBudgetBytes) > 0) {
                    reportBudgetBytes = Number(message.reportBudgetBytes)
                    enqueue(async () => {
                        const trimmed = await trimReportCache(reportBudgetBytes, message.keepFileId ? [message.keepFileId] : [])
                        if (trimmed.evicted.length) post({ type: 'cacheEvicted', fileIds: trimmed.evicted, bytes: trimmed.bytes })
                    })
                }
                break

            case 'cacheStats':
                enqueue(() => sendCacheStats(message))
                break

            case 'clearReportCache':
                enqueue(() => clearReportCache(message))
                break

            case 'cancel':
                cancelled.add(message.fileId)
                break

            case 'drop':
                live.delete(message.fileId)
                break

            default:
                break
        }
    }
}
